import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputLeftAddon,
  Text,
} from '@chakra-ui/react';
import { useContext, useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { MarketplaceContext } from 'src/contexts/marketplace';
import useRequest from 'src/utils/hooks/useRequest';
import { useCustomClipboard } from 'src/utils/marketplaceUtils';
import { DNSInfo, DigestDNSInfo } from 'src/api/v1-api/helpers';
import { getMarketplaceDomain } from 'src/utils/domainUtils';

interface ApexDomainEmailProps {
  apexDomain: string;
  emailStatus: string;
  digestStatus: string;
  onChange?: (state: EmailDnsUiState) => void;
}

export interface EmailDnsUiState {
  supportEmail: string;
  emailDomain: string;
  digestEmail: string;
  digestDomain: string;
  emailDnsPresent: boolean;
  digestDnsPresent: boolean;
  preDnsEdited: boolean;
  emailUsernameChangedAfterDns: boolean;
  emailDomainChangedAfterDns: boolean;
  digestUsernameChangedAfterDns: boolean;
  digestDomainChangedAfterDns: boolean;
}

export interface ApexDomainEmailHandle {
  resetDomains: (opts?: { email?: boolean; digest?: boolean }) => void;
  generateChangedDns: () => Promise<void>;
  getUiState: () => EmailDnsUiState;
}

const CopyField = ({ text }: { text: string }) => {
  const { onCopy, hasCopied } = useCustomClipboard(text);
  return (
    <>
      <Text fontSize="xs" wordBreak="break-all">
        {text}
      </Text>
      <Button size="xs" onClick={onCopy}>
        {hasCopied ? 'Copied!' : 'Copy'}
      </Button>
    </>
  );
};

const ApexDomainEmail = forwardRef<ApexDomainEmailHandle, ApexDomainEmailProps>((props, ref) => {
  const { apexDomain, emailStatus, digestStatus, onChange } = props;
  const { api } = useRequest();
  const { marketplace, setMarketplace } = useContext(MarketplaceContext);
  const domain = marketplace?.domain;
  const hasDnsRecord = (
    record?: { type?: string; host?: string; data?: string } | null,
  ): boolean => {
    if (!record) return false;
    const { type, host, data } = record;
    return [type, host, data].some((v) => typeof v === 'string' && v.trim() !== '');
  };
  const [editEmailConfig, setEditEmailConfig] = useState(false);
  const [editDigestConfig, setEditDigestConfig] = useState(false);
  const [emailDomain, setEmailDomain] = useState(
    domain?.config?.email_config?.domain || domain?.domain || '',
  );
  const [digestDomain, setDigestDomain] = useState(
    domain?.config?.digest_config?.domain || domain?.domain || '',
  );
  const [supportEmail, setSupportEmail] = useState(
    domain?.config?.email_config?.email || 'support',
  );
  const [digestEmail, setDigestEmail] = useState(domain?.config?.digest_config?.email || 'digest');

  const [isGeneratingDNS, setIsGeneratingDNS] = useState(false);
  const emailSlug = marketplace?.slug.replace(' ', '-').toLowerCase() ?? '';
  const emailConfig = domain?.config?.email_config;
  const emailConfigDNS = emailConfig && emailConfig.dns ? (emailConfig.dns as DNSInfo) : null;
  const digestConfig = domain?.config?.digest_config;
  const digestConfigDNS =
    digestConfig && digestConfig.dns ? (digestConfig.dns as DigestDNSInfo) : null;

  const marketplaceDomain = getMarketplaceDomain(marketplace);

  // Determine if this should use the existing separate experience vs unified experience
  // Show existing experience only if email DNS is already active and digest doesn't exist or is inactive
  const isNewUser = !(emailStatus === 'active' && (!digestConfig || digestStatus !== 'active'));

  // For new users, unified status requires both email and digest to be active
  const unifiedEmailStatus = isNewUser
    ? emailStatus === 'active' && digestStatus === 'active'
      ? 'active'
      : emailStatus === 'pending' || digestStatus === 'pending'
      ? 'pending'
      : 'inactive'
    : emailStatus;

  // Baseline values used to determine changes pre/post DNS
  const baselineSupportEmail = useMemo(
    () => domain?.config?.email_config?.email || 'support',
    [domain?.config?.email_config?.email],
  );
  const baselineEmailDomain = useMemo(
    () => domain?.config?.email_config?.domain || domain?.domain || '',
    [domain?.config?.email_config?.domain, domain?.domain],
  );
  const baselineDigestEmail = useMemo(
    () => domain?.config?.digest_config?.email || 'digest',
    [domain?.config?.digest_config?.email],
  );
  const baselineDigestDomain = useMemo(
    () => domain?.config?.digest_config?.domain || domain?.domain || '',
    [domain?.config?.digest_config?.domain, domain?.domain],
  );

  const emailDnsPresent =
    !!emailConfigDNS &&
    (hasDnsRecord(emailConfigDNS.mail_server) ||
      hasDnsRecord(emailConfigDNS.subdomain_spf) ||
      hasDnsRecord(emailConfigDNS.dkim));
  const digestDnsPresent =
    !!digestConfigDNS &&
    (hasDnsRecord(digestConfigDNS.mail_cname) ||
      hasDnsRecord(digestConfigDNS.dkim1) ||
      hasDnsRecord(digestConfigDNS.dkim2));

  const supportUsernameChanged = supportEmail !== baselineSupportEmail;
  const emailDomainChanged = emailDomain !== baselineEmailDomain;
  const digestUsernameChanged = digestEmail !== baselineDigestEmail;
  const digestDomainChanged = digestDomain !== baselineDigestDomain;

  const preDnsEdited =
    (!emailDnsPresent && (supportUsernameChanged || emailDomainChanged)) ||
    (!digestDnsPresent && (digestUsernameChanged || digestDomainChanged));

  const emailUsernameChangedAfterDns = emailDnsPresent && supportUsernameChanged;
  const emailDomainChangedAfterDns = emailDnsPresent && emailDomainChanged;
  const digestUsernameChangedAfterDns = digestDnsPresent && digestUsernameChanged;
  const digestDomainChangedAfterDns = digestDnsPresent && digestDomainChanged;

  useEffect(() => {
    if (onChange) {
      onChange({
        supportEmail,
        emailDomain,
        digestEmail,
        digestDomain,
        emailDnsPresent,
        digestDnsPresent,
        preDnsEdited,
        emailUsernameChangedAfterDns,
        emailDomainChangedAfterDns,
        digestUsernameChangedAfterDns,
        digestDomainChangedAfterDns,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    supportEmail,
    emailDomain,
    digestEmail,
    digestDomain,
    emailDnsPresent,
    digestDnsPresent,
    preDnsEdited,
    emailUsernameChangedAfterDns,
    emailDomainChangedAfterDns,
    digestUsernameChangedAfterDns,
    digestDomainChangedAfterDns,
  ]);

  useImperativeHandle(ref, () => ({
    resetDomains: (opts?: { email?: boolean; digest?: boolean }) => {
      if (!opts || opts.email !== false) {
        setEmailDomain(baselineEmailDomain);
      }
      if (!opts || opts.digest !== false) {
        setDigestDomain(baselineDigestDomain);
      }
    },
    generateChangedDns: async () => {
      if (emailDomainChangedAfterDns) {
        await generateApexEmail();
      }
      if (digestDomainChangedAfterDns) {
        await generateApexDigest();
      }
    },
    getUiState: () => ({
      supportEmail,
      emailDomain,
      digestEmail,
      digestDomain,
      emailDnsPresent,
      digestDnsPresent,
      preDnsEdited,
      emailUsernameChangedAfterDns,
      emailDomainChangedAfterDns,
      digestUsernameChangedAfterDns,
      digestDomainChangedAfterDns,
    }),
  }));

  const generateApexEmail = async () => {
    if (!marketplace) return;
    try {
      const response = await api.marketplace.generate_apex_email_dns(marketplace.id, {
        email_domain: emailDomain,
        email: supportEmail,
        email_or_digest: 'email',
      });
      setEditEmailConfig(false);
      setMarketplace({
        ...marketplace,
        domain: response,
      });
    } catch (error) {
      console.error('Error checking DNS for support:', error);
    }
  };

  const generateApexDigest = async () => {
    if (!marketplace) return;
    try {
      const response = await api.marketplace.generate_apex_email_dns(marketplace.id, {
        email_domain: digestDomain,
        email: digestEmail,
        email_or_digest: 'digest',
      });
      setEditDigestConfig(false);
      setMarketplace({
        ...marketplace,
        domain: response,
      });
    } catch (error) {
      console.error('Error checking DNS for digest:', error);
    }
  };

  const generateUnifiedEmailDNS = async () => {
    if (!marketplace) return;

    setIsGeneratingDNS(true);
    try {
      // First generate email DNS
      const emailResponse = await api.marketplace.generate_apex_email_dns(marketplace.id, {
        email_domain: emailDomain,
        email: supportEmail,
        email_or_digest: 'email',
      });

      // Then generate digest DNS using the updated marketplace state
      const digestResponse = await api.marketplace.generate_apex_email_dns(marketplace.id, {
        email_domain: digestDomain,
        email: digestEmail,
        email_or_digest: 'digest',
      });

      // After generating both DNS configs, check both email and digest DNS to get complete config data
      // The check endpoints return the actual domain config, unlike the generate endpoints
      await api.marketplace.check_email_dns.get(marketplace.id, 'email');
      const finalDomainResponse = await api.marketplace.check_email_dns.get(
        marketplace.id,
        'digest',
      );

      setEditEmailConfig(false);
      setEditDigestConfig(false);

      setMarketplace({
        ...marketplace,
        domain: finalDomainResponse,
      });
    } catch (error) {
      console.error('Error generating unified DNS:', error);
    } finally {
      setIsGeneratingDNS(false);
    }
  };

  const checkEmailDNS = async (email_or_digest: 'email' | 'digest') => {
    if (!marketplace) return;
    try {
      const response = await api.marketplace.check_email_dns.get(marketplace.id, email_or_digest);
      setMarketplace({
        ...marketplace,
        domain: response,
      });
    } catch (error) {
      console.error('Error checking DNS:', error);
    }
  };

  const checkUnifiedEmailDNS = async () => {
    if (!marketplace) return;
    try {
      // Check both email and digest DNS
      await Promise.all([checkEmailDNS('email'), checkEmailDNS('digest')]);
    } catch (error) {
      console.error('Error checking unified DNS:', error);
    }
  };

  const UnifiedDNSInstructions = () => {
    return (
      <Box pt={4}>
        <Text pt="2" pb="2" fontWeight="semibold" fontSize="lg">
          Email DNS Instructions
        </Text>
        <Text pt="2">
          To activate emails coming from your domain, the following DNS records must be added to
          your domain host. After adding the records, you may need to wait a few minutes before our
          DNS recognizes the changes.
        </Text>

        <Text pt="4" pb="2" fontWeight="semibold" fontSize="md">
          Support Email DNS Records
        </Text>
        <Grid templateColumns="repeat(7, 1fr)" gap={6} pt="2">
          <GridItem>
            <Text fontWeight="semibold">Type</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Text fontWeight="semibold">Hostname</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Text fontWeight="semibold">Data</Text>
          </GridItem>

          <GridItem>
            <Text fontSize="xs">{(emailConfigDNS?.mail_server?.type || '').toUpperCase()}</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={emailConfigDNS?.mail_server?.host || ''} />
            </Flex>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={emailConfigDNS?.mail_server?.data || ''} />
            </Flex>
          </GridItem>

          <GridItem>
            <Text fontSize="xs">{(emailConfigDNS?.subdomain_spf?.type || '').toUpperCase()}</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={emailConfigDNS?.subdomain_spf?.host || ''} />
            </Flex>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={emailConfigDNS?.subdomain_spf?.data || ''} />
            </Flex>
          </GridItem>

          <GridItem>
            <Text fontSize="xs">{(emailConfigDNS?.dkim?.type || '').toUpperCase()}</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={emailConfigDNS?.dkim?.host || ''} />
            </Flex>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={emailConfigDNS?.dkim?.data || ''} />
            </Flex>
          </GridItem>

          <GridItem>
            <Text fontSize="xs">TXT</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={`_dmarc.${emailConfig?.domain || domain?.domain}`} />
            </Flex>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text="v=DMARC1; p=none;" />
            </Flex>
          </GridItem>
        </Grid>

        <Text pt="6" pb="2" fontWeight="semibold" fontSize="md">
          Digest Email DNS Records
        </Text>
        <Grid templateColumns="repeat(7, 1fr)" gap={6} pt="2">
          <GridItem>
            <Text fontWeight="semibold">Type</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Text fontWeight="semibold">Hostname</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Text fontWeight="semibold">Data</Text>
          </GridItem>

          <GridItem>
            <Text fontSize="xs">{(digestConfigDNS?.mail_cname?.type || '').toUpperCase()}</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={digestConfigDNS?.mail_cname?.host || ''} />
            </Flex>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={digestConfigDNS?.mail_cname?.data || ''} />
            </Flex>
          </GridItem>

          <GridItem>
            <Text fontSize="xs">{(digestConfigDNS?.dkim1?.type || '').toUpperCase()}</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={digestConfigDNS?.dkim1?.host || ''} />
            </Flex>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={digestConfigDNS?.dkim1?.data || ''} />
            </Flex>
          </GridItem>

          <GridItem>
            <Text fontSize="xs">{(digestConfigDNS?.dkim2?.type || '').toUpperCase()}</Text>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={digestConfigDNS?.dkim2?.host ?? ''} />
            </Flex>
          </GridItem>
          <GridItem colSpan={3}>
            <Flex gap="3">
              <CopyField text={digestConfigDNS?.dkim2?.data || ''} />
            </Flex>
          </GridItem>
        </Grid>
      </Box>
    );
  };

  return (
    <>
      <Text>
        By adding additional DNS records you can enable Loma to send emails on behalf of your
        domain. This will allow transaction emails to come from:
      </Text>
      <Flex
        gap="5"
        p={{ base: 4, md: 4 }}
        w="100%"
        direction={{ base: 'column-reverse', md: 'row' }}
      >
        <Text fontSize="md">
          <b>{supportEmail}</b>@<b>{emailConfig?.domain || apexDomain}</b>
        </Text>
        <Text fontSize="md">vs.</Text>
        <Text fontSize="md">
          support+{emailSlug}@{marketplaceDomain}
        </Text>
      </Flex>
      <Text>
        Once this change is made, customers responding to their transaction emails will go directly
        to the support email address on your domain. You must configure your email server to handle
        this address. To continue having Loma manage all support issues, have the support email
        address forward to &quot;support+{emailSlug}@{marketplaceDomain}&quot;.
      </Text>
      <Text>
        You may also create a &quot;support&quot; user in your email system or an email alias to
        route incoming emails to your normal email address. In both cases, you will be responsible
        for handling any support inquiries and Loma won’t know about any inquiries unless your
        forward the email to &quot;support+{emailSlug}@{marketplaceDomain}&quot;.
      </Text>
      <Text lineHeight="1.5" fontWeight="semibold" fontSize="16px" pt={4}>
        Custom Support Email Address
      </Text>
      <Text>
        You may change the email account that sends support emails which will handle email replies
        from users. By default, the email account used is &quot;support&quot;.
      </Text>
      <Text pb={2}>
        Emails will come from the subdomain or domain mapped to your Loma Marketplace. In some
        cases, you may wish to host Loma on a subdomain (like &quot;market.mysite.com&quot;) but
        have emails come from your root domain (like &quot;support@mysite.com&quot;). You may edit
        both values below then click &quot;Generate Email DNS&quot; to save and refresh the DNS
        values if necessary.
      </Text>
      <InputGroup>
        <Input
          name="support_email"
          type="text"
          value={supportEmail}
          onChange={(e) => setSupportEmail(e.target.value)}
        />
        <InputLeftAddon>@</InputLeftAddon>
        <Input
          name="email_domain"
          type="text"
          value={emailDomain}
          onChange={(e) => setEmailDomain(e.target.value)}
        />
      </InputGroup>
      <Text pt={2} pb={4}>
        Transactional emails from Loma to your marketplace users will appear to come from:{' '}
        <b>{supportEmail}</b>@<b>{emailDomain}</b>
      </Text>

      <InputGroup>
        <Input
          name="digest_email"
          type="text"
          value={digestEmail}
          onChange={(e) => setDigestEmail(e.target.value)}
        />
        <InputLeftAddon>@</InputLeftAddon>
        <Input
          name="digest_domain"
          type="text"
          value={digestDomain}
          onChange={(e) => setDigestDomain(e.target.value)}
        />
      </InputGroup>
      <Text pt={2} pb={4}>
        Digest emails from Loma to your digest subscribers will appear to come from:{' '}
        <b>{digestEmail}</b>@<b>{digestDomain}</b>
      </Text>

      <Divider />

      {/* NEW USER EXPERIENCE: Unified email DNS setup */}
      {isNewUser ? (
        <>
          {emailDnsPresent && digestDnsPresent ? (
            <>
              <Text pt="2" fontWeight="medium">
                Email DNS Status
              </Text>
              <Flex gap="6" alignItems="center">
                {unifiedEmailStatus === 'pending' && (
                  <Text fontWeight="bold" color="gray" pl="2">
                    Pending
                  </Text>
                )}
                {unifiedEmailStatus === 'active' && (
                  <Text fontWeight="bold" color="green" pl="2">
                    Active
                  </Text>
                )}
                {(!unifiedEmailStatus || unifiedEmailStatus === 'inactive') && (
                  <Text fontWeight="bold" color="red" pl="2">
                    Inactive
                  </Text>
                )}
                {unifiedEmailStatus !== 'active' && (
                  <Button onClick={() => checkUnifiedEmailDNS()} isDisabled={!unifiedEmailStatus}>
                    Check Email DNS to Activate
                  </Button>
                )}
              </Flex>

              <UnifiedDNSInstructions />
            </>
          ) : (
            <>
              <Text>
                Generate DNS records to enable both support and digest emails from your domain.
                After generating, the DNS records to add will appear under &quot;Email DNS
                Instructions&quot;.
              </Text>

              <Button
                onClick={() => generateUnifiedEmailDNS()}
                isLoading={isGeneratingDNS}
                loadingText="Generating DNS..."
              >
                Generate Email DNS
              </Button>
            </>
          )}
        </>
      ) : (
        /* EXISTING USER EXPERIENCE: Separate email and digest DNS setup with unified instructions */
        <>
          <Text lineHeight="1.5" fontWeight="semibold" fontSize="16px" pt={4}>
            Support Email DNS
          </Text>
          {emailConfig && !editEmailConfig ? (
            <>
              <Button onClick={() => setEditEmailConfig(true)}>Edit Email Domain</Button>

              <Text pt="2" fontWeight="medium">
                Support Email DNS Status
              </Text>
              <Flex gap="6" alignItems="center">
                <Text fontWeight="bold" color="green" pl="2">
                  Active
                </Text>
              </Flex>

              <UnifiedDNSInstructions />
            </>
          ) : (
            <>
              <Text>
                You must generate DNS records for use and upon changing your support email. After
                generating, the DNS records to add will appear under &quot;Support Email DNS
                Instructions&quot;.
              </Text>

              <Button onClick={() => generateApexEmail()}>Generate Support Email DNS</Button>
            </>
          )}

          <Divider />

          <Text lineHeight="1.5" fontWeight="semibold" fontSize="16px" pt={4}>
            Digest Email DNS
          </Text>
          {digestConfig && !editDigestConfig ? (
            <>
              <Button onClick={() => setEditDigestConfig(true)}>Edit Digest Email Domain</Button>

              <Text pt="2" fontWeight="medium">
                Digest Email DNS Status
              </Text>
              <Flex gap="6" alignItems="center">
                {digestStatus === 'pending' && (
                  <Text fontWeight="bold" color="gray" pl="2">
                    Pending
                  </Text>
                )}
                {digestStatus === 'active' && (
                  <Text fontWeight="bold" color="green" pl="2">
                    Active
                  </Text>
                )}
                {(!digestStatus || digestStatus === 'inactive') && (
                  <Text fontWeight="bold" color="red" pl="2">
                    Inactive
                  </Text>
                )}
                {digestStatus !== 'active' && (
                  <Button onClick={() => checkEmailDNS('digest')} isDisabled={!digestStatus}>
                    Check Digest DNS to Activate
                  </Button>
                )}
              </Flex>
            </>
          ) : (
            <>
              <Text>
                You must generate DNS records for use and upon changing your digest email. After
                generating, the DNS records to add will appear under &quot;Digest Email DNS
                Instructions&quot;.
              </Text>

              <Button onClick={() => generateApexDigest()}>Generate Digest Email DNS</Button>
            </>
          )}
        </>
      )}
    </>
  );
});

ApexDomainEmail.displayName = 'ApexDomainEmail';

export default ApexDomainEmail;
